import React, {useEffect, useRef, useState } from "react";
import { KafkaEvent, kafkaInstance, SourceLayer } from "../Interfaces/IKafkaEvents";
import { HubPathRouting } from "../../../HubFramework/pathBuilder";
import { listModuleInstances } from "../../../BackendFacade/moduleCalls";
import { IListStaticModule } from "../../../BackendFacade/IStaticModules";
import { IModule } from "../../../BackendFacade/IModuleInstances";
import { getEventLogs } from "../BackendFacade/apiCalls";
import { removeDuplicates} from "../../../Utils/transformer";
import { GlobalDictionary } from "../../../Utils/globalDictionary";
import {buildSourceTreeUsingEventCountApi, fixModuleName, getLineage} from "../Utils/eventEvaluation";
import { useMain } from "../../../Utils/SessionControls/mainContext";
import SourceTree from "./Components/SourceTree";
import LogsTable from "./Components/LogsTable";
import { odxIconSets } from "../../../OdxComponents/constants";
import OdxIcon from "../../../OdxComponents/NativeOdx/Odx-Icon";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import OdxMainFilterGroup from "../../../OdxComponents/NativeOdx/Odx-MainFilterGroup";
import OdxSelect, { OdxSelectOptionProps } from "../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import OdxInputField from "../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import DateRangeModal from "./Components/DateRangeModal";
import { OptionOIS } from "../../DOOP";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import OdxContentBox from "../../../OdxComponents/NativeOdx/Odx-Contentbox";

interface datePicker {
    from: string;
    to: string;
}

const LogOverview: React.FC = () => {
    // Date filters, search and log level (already existing)
    const dayBeforeYesterday= new Date(new Date().setDate(new Date().getDate() - 2)).toISOString().substring(0, 10);
    const today = new Date().toISOString().substring(0, 10);

    const [datePicker, setDatePicker] = useState<datePicker>({ from: dayBeforeYesterday, to: today });

    const [searchTerm, setSearchTerm] = useState<string>("");
    const [excludedTopics, setExcludedTopics] = useState<OdxSelectOptionProps[]>([]);
    const availableLogLevelFilters = [
        { label: "Info", value: "Info", disabled: false },
        { label: "Warning", value: "Warning", disabled: false },
        { label: "Error", value: "Error", disabled: false },
    ] as OdxSelectOptionProps[];
    const [currentLogLevelFilters, setCurrentLogLevelFilters] = useState<string[]>([
        availableLogLevelFilters[1].value,
        availableLogLevelFilters[2].value,
    ]);

    const [events, setEvents] = useState<KafkaEvent[]>([]);
    const [topics, setTopics] = useState<string[]>([]);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);

    // SOURCE TREE
    const [sourceFilterViaSourceTree, setSourceFilterViaSourceTree] = useState<string>("");
    const [currentEventLayer, setCurrentEventLayer] = useState<SourceLayer>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const { setLoading, isLoading, createErrorToast } = useMain();
    const baseId = 'dams-logs';

    useModuleViewTracking('dams-main')

    useEffect(() => {
        GlobalDictionary.getProjects().then(() => {

            getAllData();
        });
    }, []);

    // some ODX magic for Grid and layout stuff --> Ben has upgrade for this!
    useEffect(() => {
        if (containerRef.current) {
            const elements = containerRef.current.querySelectorAll("[data-odxlayout]");
            elements.forEach((element) => {
                const dataOdxlayout = element.getAttribute("data-odxlayout");
                if (dataOdxlayout) {
                    element.setAttribute("odxlayout", dataOdxlayout);
                    element.removeAttribute("data-odxlayout");
                }
            });
        }
    }, [isLoading, currentEventLayer]);

    useEffect(() => {
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;
        if (datePattern.test(datePicker.from) && datePattern.test(datePicker.to)) {
            getAllData();
        }
    }, [datePicker]);

    useEffect(() => {
        // update data, pull again from network -> going down to clientID level will massively improve results, while going up afterwards need to re-pull data from other levels
        getDataForSubTree();
    }, [sourceFilterViaSourceTree]);

    const getDataForSubTree = async () => {
        const instances = await prepareLogInfos();

        let filteredKafkaInstances = instances.filter(
            instance => !excludedTopics.some(exclusion => exclusion.value === instance.eventLogTopicName)
        );

        setTopics(removeDuplicates(instances.map((instance) => instance.eventLogTopicName)));

        const currentProjectId = HubPathRouting.currentProject ? HubPathRouting.currentProject.id : "null";

        // filtering using source tree needs to be smarter:
        //  example source: https://draeger.com/DevSecOps/IDMS/HCA/users
        //  currently, we only see the following parts: IDMS -> HCA -> users
        //  when the user clicks on IDMS, we can be smart: only do API calls for the two IDMS topics! no need to check the DOOP topic, because none of its events will have this source!
        //  when the user clicks HCA: specify the HCA-ID as clientProjectNameFilter aka. linkedId!
        //  when the user clicks users (especially on other, smaller apps): there is no query to filter by this specific source,
        //      it'd need an indexed DB contains search, which we dont have. meaning all we can do is pull ALL IDMS events and
        //      HOPE we get some of this specific app, and not just HCA (which is hard because HCA is like 99% of IDMS logs right now)
        //      --> to help with this: use the clientProjectNameFilter!!
        //  -> EVERY 1st level is a topic and EVERY 2nd level is clientProjectNameFilter
        const sourceParts = sourceFilterViaSourceTree.split("/").filter(x => x != "");
        sourceParts.splice(0, 1); // remove the first part (devsecops)
        if (sourceParts.length == 1)
        {
            // we know the module/app/tool part that the user wants -> that is equivalent to one or two topics! lets only query these topics!
            const topics = translateSourcePartToTopicList(sourceParts[0]);
            filteredKafkaInstances = filteredKafkaInstances.filter(x => topics.indexOf(x.eventLogTopicName) != -1);
        }
        if (sourceParts.length >= 2)
        {
            // we know the topic(s) AND client-ID that the user wants -> those are two valid query-params for the API! lets use them and skip everything else!
            const topics = translateSourcePartToTopicList(sourceParts[0]);
            const clientId = sourceParts[1];
            filteredKafkaInstances = topics.map(t => {
                return { linkedId: clientId, eventLogTopicName: t} as kafkaInstance;
            });
        }

        // get logs for each topic and logLevel
        const eventsBuffer: KafkaEvent[] = [];
        const promises = [];
        for (const instance of filteredKafkaInstances) {
            for (const loglevel of currentLogLevelFilters) {
                const promise = fetchLogs(currentProjectId, instance.eventLogTopicName, instance.linkedId, loglevel, eventsBuffer);
                promises.push(promise);
            }
        }
        await Promise.all(promises);
        setEvents(eventsBuffer);
    }

    const getAllData = async () => {
        setLoading(true);

        await prepareLogInfos();


        const layers = await buildSourceTreeUsingEventCountApi(datePicker.from, datePicker.to, HubPathRouting.currentProject?.id ? HubPathRouting.currentProject.id : "null");
        setCurrentEventLayer(layers);

        setLoading(false);
    };

    const translateSourcePartToTopicList = (sourcePart: string) : string[] => {
        sourcePart = sourcePart.toLowerCase();
        // realistically, only one of these topics is always meant, but we have no idea which event comes from which topic at this point.
        if (sourcePart == "idms") return ["devsecops.idms-events.prod.v1.json", "devsecops.idms-events.stage.v1.json"];
        if (sourcePart == "doop") return ["devsecops.doop-events.prod.v1.json", "devsecops.doop-events.staging.v1.json"];
        if (sourcePart == "keychanger") return ["devsecops.keychanger.events.v1.json"];
        if (sourcePart == "dpas") return ["devsecops.dpas-events.v1.json"];
        if (sourcePart == "hub") return ["devsecops.hub-events.stage.v1.json"];
        if (sourcePart == "devplatformmng") return ["devsecops.DevPlatformMng-events.prod.v1.json"];
        // todo: we could use the staticModuleList to filter if the (display?-) name contains the source and use the topicList from that object
        throw new Error("unknown source name: " + sourcePart);
    }

    const fetchLogs = async (projectId: string, kafkaTopic: string, linkedId: string, logLevel: string, resultBuffer: KafkaEvent[]) => {
        let continuationToken = null;
        let moreDataAvailable = true;
        let collectedEvents: KafkaEvent[] = [];

        while (moreDataAvailable) { // todo: only one page!!! otherwise enabling INFO logs will load FOREVER!!! load more page(s) when user wants to!
            const response = await getEventLogs(
                projectId,
                kafkaTopic,
                linkedId,
                {
                    startDay: convertDateToDayOfYear(datePicker.from),
                    startYear: convertDateToYear(datePicker.from),
                    endDay: convertDateToDayOfYear(datePicker.to),
                    endYear: convertDateToYear(datePicker.to),
                },
                1000,
                logLevel,
                continuationToken
            );

            if (response.success) {
                const { value, continuationToken: newContinuationToken } = response.data;
                collectedEvents = collectedEvents.concat(value);
                continuationToken = newContinuationToken;
                moreDataAvailable = !!continuationToken;
            } else {
                createErrorToast(response.message, "Error" + kafkaTopic);
                moreDataAvailable = false;
            }
        }

        collectedEvents.forEach(x => resultBuffer.push(x));

        return collectedEvents;
    };

    const getModuleAndKafkaInstances = async (staticModules: IListStaticModule[]): Promise<kafkaInstance[]> => {
        let moduleInstances: IModule[];
        let kafkaInstances: kafkaInstance[];

        if (HubPathRouting.currentProject) {
            moduleInstances = await GlobalDictionary.get(
                HubPathRouting.currentProject.id +
                GlobalDictionary.MODULEINSTANCES
            );
            if (!moduleInstances) {
                await listModuleInstances(HubPathRouting.currentProject.id).then((data) => {
                    GlobalDictionary.set(
                        HubPathRouting.currentProject.id +
                        GlobalDictionary.MODULEINSTANCES,
                        data.data.value
                    );
                    moduleInstances = data.data.value;
                });
            }

            if (HubPathRouting.currentModule) {
                moduleInstances = moduleInstances.filter(
                    (instance) =>
                        instance.moduleInstanceId ===
                        HubPathRouting.currentModule.id
                );
            }

            kafkaInstances = moduleInstances
                .map((instance) => {
                    const staticModule = staticModules.find(
                        (staticModule) =>
                            staticModule.staticModuleId === instance.staticModuleId &&
                            staticModule.eventLogTopicName !== null
                    );
                    if (!staticModule) return undefined;
                    return {
                        linkedId: instance.linkedId.projectId,
                        eventLogTopicName: staticModule.eventLogTopicName,
                    };
                })
                .filter((instance) => instance !== undefined) as kafkaInstance[];
        } else {
            const projects = await GlobalDictionary.getProjects();

            const moduleListProms = projects.map(async (project) => {
                const data = await listModuleInstances(project.projectId);

                if (data.success) {
                    const modules : IModule[] = data.data.value;

                    // add projectId to each module object // todo: can't we do that in listModuleInstances???
                    modules.forEach((x: IModule) => x.projectId = project.projectId);

                    return modules;
                } else {
                    createErrorToast("Error", data.message);
                    return [];
                }
            });

            const moduleLists = await Promise.all(moduleListProms);
            moduleInstances = moduleLists.flat();

            kafkaInstances = staticModules
                .map((staticModule) => {
                    if (!staticModule.eventLogTopicName) return null;
                    return {
                        linkedId: null,
                        eventLogTopicName: staticModule.eventLogTopicName,
                    };
                })
                .filter((instance) => instance != null) as kafkaInstance[];
        }

        GlobalDictionary.set("kafkaModuleInstances", moduleInstances);
        return kafkaInstances;
    };

    const prepareLogInfos = async (): Promise<kafkaInstance[]> => {
        const staticModules: IListStaticModule[] = await GlobalDictionary.getStaticModules();
        let kafkaInstances: kafkaInstance[] = await getModuleAndKafkaInstances(staticModules);

        // navinta exception
        if (HubPathRouting.currentProject && (HubPathRouting.currentProject.id == "a25f1523443bb48f88742295a45310d01" || HubPathRouting.currentProject.id == "a907fb0f8627e40f2b32e6ce615f0630f")) {
            kafkaInstances.push({linkedId: null, eventLogTopicName: "cpqnavintaeventsprodv1"})
            kafkaInstances.push({linkedId: null, eventLogTopicName: "cpqnavintaeventsstagev1"})
        }

        if (!HubPathRouting.currentModule) {
            kafkaInstances.push({
                linkedId: HubPathRouting.currentProject
                    ? HubPathRouting.currentProject.id
                    : null,
                eventLogTopicName: "devsecops.DevPlatformMng-events.prod.v1.json",
            });
            if (
                window.location.href.includes("stage") ||
                window.location.href.includes("localhost")
            ) {
                kafkaInstances.push({
                    linkedId: HubPathRouting.currentProject
                        ? HubPathRouting.currentProject.id
                        : null,
                    eventLogTopicName: "devsecops.hub-events.stage.v1.json",
                });
            } else {
                kafkaInstances.push({
                    linkedId: HubPathRouting.currentProject
                        ? HubPathRouting.currentProject.id
                        : null,
                    eventLogTopicName: "devsecops.hub-events.prod.v1.json",
                });
            }
        }

        kafkaInstances = removeDuplicates(kafkaInstances, x => x.linkedId + "--" + x.eventLogTopicName);

        return kafkaInstances;
    };

    const convertDateToDayOfYear = (dateString: string): number => {
        const date = new Date(dateString);
        const start = new Date(date.getFullYear(), 0, 0);
        const diff =
            date.getTime() -
            start.getTime() +
            (start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000;
        const oneDay = 1000 * 60 * 60 * 24;
        return Math.floor(diff / oneDay);
    };

    const convertDateToYear = (dateString: string): number => {
        const date = new Date(dateString);
        return date.getFullYear();
    };

    const displayChildLayer = (newLayer: SourceLayer) => {
        const path = getLineage(newLayer).slice(1).map(x => x.rawSource).join("/")
        setSourceFilterViaSourceTree(path);
        setCurrentEventLayer(newLayer);
        // update data via useEffect()
    };

    const goOneLevelUpInSourceTree = () => {
        goUpInSourceTree(1);
    };

    const goUpInSourceTree = (amount : number) => {
        let target = currentEventLayer;
        for (let i = 0; i < amount; i++) {
            if (target.parent == null) createErrorToast("Root level reached");
            else target = target.parent;
        }
        displayChildLayer(target);
    }

    // if targetDepth is 0, we want to go to the root level
    const navigateToLayerInSourceTree = (targetDepth: number) => {
        const currentDepth = getLineage(currentEventLayer).length - 1;
        const remainingHops = currentDepth - targetDepth;
        goUpInSourceTree(remainingHops);
    };

    if (!currentEventLayer) {
        return <></>;
    }

    const convertTopicsToOptionsList = (kafkaTopics: string[]): OptionOIS[] => {
        return kafkaTopics.map(topic => ({
            value: topic,
            label: topic.replace("devsecops.", "").replace(".v1.json", "")
        }));
    };

    return (
        <div id={`${baseId}-view`}>
            <DateRangeModal
                isOpen={isDateModalOpen}
                from={datePicker.from}
                to={datePicker.to}
                onClose={() => setIsDateModalOpen(false)}
                onApply={(from: string, to: string) => {
                    setDatePicker({ from, to });
                    setIsDateModalOpen(false);
                }}
            />

            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>
            <div ref={containerRef} id={`${baseId}-content`}>
                <OdxAreaHeader odxSize={"large"} text={"Logs"} className={"!pl-0 !pr-0 mb-4"} id={`${baseId}-header`}/>

                <div data-odxLayout="grid vertical-end mb-4">
                    <div data-odxLayout="4">
                        {/* You can add a button here to toggle the source tree if needed */}
                    </div>
                    <div data-odxLayout="offset-8"></div>
                    <div data-odxLayout="4 vertical-end" className={"justify-self-end"}>
                        <OdxMainFilterGroup>
                            <OdxInputField
                                isRequired={false}
                                label={""}
                                id={`${baseId}-search`}
                                placeholder={"Search logs"}
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                }}
                            />
                            <OdxSelect
                                options={availableLogLevelFilters}
                                value={currentLogLevelFilters}
                                label={"Log Level"}
                                isRequired={true}
                                isMulti={true}
                                onChange={(selectedValues: string[]) => {
                                    setCurrentLogLevelFilters(availableLogLevelFilters.filter(option => selectedValues.includes(option.value)).map(x => x.value));
                                }}
                                onBlur={() => getAllData()}
                                placeholder={"Select Log Levels"}
                                id={`${baseId}-loglevel-filter`}
                            />
                            <OdxSelect
                                isMulti
                                options={convertTopicsToOptionsList(topics)}
                                value={excludedTopics.map(option => option.value)}
                                label={"Exclude by Topic"}
                                onChange={(selectedValues: string[]) => {
                                    const updatedExcludedTopics = selectedValues.map(value => ({
                                        value,
                                        label: topics.find(topic => topic === value) || value,
                                    }));
                                    setExcludedTopics(updatedExcludedTopics);
                                }}
                                onBlur={() => {
                                    getAllData()
                                }}
                                placeholder={"Exclude Topics"}
                                matchReferenceWidth={false}
                                id={`${baseId}-exclude-filter`}
                            />

                            <odx-icon-button
                                id={`OdxButton_${baseId}-date-range-btn`}
                                variant="ghost"
                                icon={`${odxIconSets.core}::menu`}
                                type="button"
                                onClick={() => setIsDateModalOpen(true)}
                            />
                        </OdxMainFilterGroup>
                    </div>
                </div>

                <div className="mb-4" id={`${baseId}-breadcrumbs-bar`}>
                    {/* Breadcrumbs */}
                    <div className="breadcrumbs">
                        <odx-icon-button
                            id={`OdxButton_${baseId}-breadcrumb-back`}
                            variant="ghost"
                            icon={`${odxIconSets.core}::chevron-left`}
                            onClick={goOneLevelUpInSourceTree}
                        />
                        {getLineage(currentEventLayer).map((layer, index, arr) => (
                            <React.Fragment key={index}>
                                {index < arr.length - 1 ? (
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigateToLayerInSourceTree(index);
                                        }}
                                    >
                                        {fixModuleName(layer.displayString)}
                                    </a>
                                ) : (
                                    <span>{fixModuleName(layer.displayString)}</span>
                                )}
                                {index < arr.length - 1 && " > "}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className="odxGridContainer" data-odxLayout="grid">
                    {(
                        <div data-odxLayout="3">
                            <OdxContentBox
                                title="Search Tree"
                                id={`${baseId}-tree-box`}
                            >
                                <div className={"min-h-80"}>
                                    <SourceTree layer={currentEventLayer}
                                                onSelectLayer={displayChildLayer}
                                                logLevelFilter={currentLogLevelFilters}
                                    />
                                </div>
                            </OdxContentBox>
                        </div>
                    )}
                    <div data-odxLayout={"9"} id={`${baseId}-table-container`}>
                        <OdxContentBox title="Logs Table" id={`${baseId}-table-box`}>
                            <LogsTable
                                eventList={events}
                                searchTerm={searchTerm}
                                sourceFilter={sourceFilterViaSourceTree}
                            />
                        </OdxContentBox>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogOverview;
